import MenuSelector from '../../core/MenuSelector';
import PagingFilter from '../../core/PagingFilter';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { v4 as uuidv4 } from 'uuid';
import Upgrade from '../../core/Upgrade';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import InAppBar from '../../core/InAppBar';
import PrimaryButton from '../../core/PrimaryButton';
import ProjectUsers from '../../core/ProjectUsers';
import GS from '../../grafsync/grafsync';
import ActionText from '../ActionText';
import Errored from '../Errored';
import Loading from '../Loading';
import Paging from '../Paging';
import { globalStyles } from '../style/gstheme';
import { STATUS_ACCEPTED, STATUS_DRAFT, STATUS_PENDING, STATUS_REJECTED } from '../utils/constants';
import { ENTITLEMENTS } from '../../application-core/constants';
import { makeCancelable, sortObjects, sortByProp } from '../utils/utils';
import ResourceDisplay from './ResourceDisplay';
import TaskDisplay from './TaskDisplay';
import SuggestionDisplay from './SuggestionDisplay';
import TaxonomySelector from '../../vocabulary/taxonomies/TaxonomySelector';
import { SUGGESTION_TYPE_OPTIONS } from '../../vocabulary/constants';


const APPROVED = STATUS_ACCEPTED;
const DRAFT = STATUS_DRAFT;
const PENDING = STATUS_PENDING;
const REJECTED = STATUS_REJECTED;
const SUGGESTIONS = 2;
const TASKS = 1;
const WORKFLOW = 0;

const styles = () => {
  return Object.assign({
    taskResults: {
      marginTop: 12,
      padding: 12,
      borderRadius: 4,
      backgroundColor: '#f5f5f5'
    },
    tasks: {
      marginRight: 30,
      marginBottom: 30,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      overflow: 'auto',
      padding: 12
    },
    taskSection: {
      padding: 12,
      borderRadius: 4,
      backgroundColor: 'white'
    }
  }, globalStyles);
};

class TaskManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      tabValue: WORKFLOW,
      page: 1,
      text: "",
      showType: "concepts",
      suggestionType: "All",
      refresh: false,
      searchID: uuidv4(),
      schemeIsActivated: undefined,
      lang: this.props.data.defaultLang,
      pageCount: undefined,
      searchCount: undefined,
      pageSize: 20,
      taskStatusCounts: undefined,
      user: props.user.id,
      isErrored: undefined,
      isLoading: true,
      resources: undefined,
      scheme: "All",
      usable: ["Managed", "Trial", "Premium"].includes(GS.getActiveSubscriptionType())
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.getData(this.state.searchID);
  }

  handleSchemeChange = async (scheme) => {
    try {
      let isWorkflowActivated = undefined;
      if (scheme !== "All") {
        await this.loadData([scheme], {
          "predicates": [
            "http://purl.org/dc/terms/title",
            "https://grafsync.graphifi.com/schema/gs/activateWorkflow"
          ]
        }).promise;
        const schemeData = GS.data(scheme, this.props.graph, false);
        isWorkflowActivated = schemeData ?
          schemeData.activateWorkflow === true
          :
          false;
      }
      this.setState({ scheme, schemeIsActivated: isWorkflowActivated }, () => {
        if (this.state.tabValue === SUGGESTIONS || isWorkflowActivated) {
          const searchID = uuidv4();
          this.setState({
            searchID,
            page: 1,
            refresh: false,
            taskStatusCounts: undefined,
            pageCount: undefined,
            searchCount: undefined,
            resources: undefined
          }, () => this.getData(searchID));
        }
        else {
          this.setState({
            page: 1,
            refresh: false,
            taskStatusCounts: undefined,
            pageCount: undefined,
            searchCount: undefined,
            resources: undefined
          });
        }
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  handleUserChange = (user) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      refresh: false,
      taskStatusCounts: undefined,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      user,
      searchID
    }, () => this.getData(searchID));
  };

  getData = (searchID) => {
    try {
      this.setState({ isLoading: true, refresh: false }, async () => {
        try {
          GS.handle("get-data");
          const data = await this.loadTasks(this.state.page, searchID);
          if (this.cancellable && this.cancellable.isCancelled()) return;
          if (data.uuid && data.uuid !== this.state.searchID) return;
          // To make life easy for sorting if there are literalForm values we create prefLabels in the default lang
          data.results.forEach(result => {
            if (result.literalForm) {
              result.prefLabel = {};
              if (Object.keys(result.literalForm) && Object.keys(result.literalForm).length > 0) {
                result.prefLabel[this.props.data.defaultLang] = result.literalForm[Object.keys(result.literalForm)[0]];
              }
            }
          });
          const sortedResources = this.state.tabValue === WORKFLOW ?
            sortObjects(data.results, "prefLabel", this.state.lang, null, false)
            :
            sortByProp(data.results, "created");
          const resourceIDs = sortedResources
            .filter(result => !result.assignedLink || (this.props.taxonomies && !this.props.taxonomies.some(t => t.id === result.assignedLink)))
            .map(result => result.assignedLink ?
              result.assignedLink
              :
              result.id);
          const suggestionsIDs = sortedResources
            .filter(result => result.assignedLink)
            .map(result => result.id);
          const allResourceIDs = [...resourceIDs, ...suggestionsIDs];
          // May be multiple suggestions for same resource
          const uniqueAllResourcesIDs = [...new Set(allResourceIDs)];
          let taskStatusCounts = undefined;
          this.loadData(uniqueAllResourcesIDs, {})
            .promise
            .then(async () => {
              if (this.state.tabValue === WORKFLOW && data.results.length > 0) {
                taskStatusCounts = await this.loadTaskCounts(sortedResources);
                if (this.cancellable && this.cancellable.isCancelled()) return;
              }
              if (data.uuid && data.uuid !== this.state.searchID) return;
              this.setState({
                resources: sortedResources,
                taskStatusCounts,
                pageCount: data.pageCount,
                searchCount: data.searchCount,
                page: data.page,
                isLoading: false
              });
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        }
        catch (e) {
          if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  doSearch(payload, searchID) {
    this.cancellable = makeCancelable(GS.search(payload, searchID));
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  loadTasks = async (page = 1, searchID) => {
    const workflowTypes = this.state.showType === "concepts" ?
      ["http://www.w3.org/2004/02/skos/core#Concept"]
      :
      ["http://www.w3.org/2008/05/skos-xl#Label"];

    const typeValues = this.state.tabValue === WORKFLOW ?
      workflowTypes
      :
      this.state.tabValue === TASKS ?
        [
          "https://grafsync.graphifi.com/schema/gs/Task",
        ]
        :
        this.state.suggestionType === "All" ?
          [
            "https://grafsync.graphifi.com/schema/vm/DeprecationSuggestion",
            "https://grafsync.graphifi.com/schema/vm/RemovalSuggestion",
            "https://grafsync.graphifi.com/schema/vm/MovingSuggestion",
            "https://grafsync.graphifi.com/schema/vm/TerminologySuggestion",
            "https://grafsync.graphifi.com/schema/vm/MergeSuggestion",
            "https://grafsync.graphifi.com/schema/vm/SplitSuggestion",
            "https://grafsync.graphifi.com/schema/vm/SpellingSuggestion",
            "https://grafsync.graphifi.com/schema/vm/NewConceptSuggestion"
          ]
          :
          ["https://grafsync.graphifi.com/schema/vm/" + this.state.suggestionType]
      ;
    let search = [
      {
        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
        value: typeValues
      }];
    if (this.state.tabValue === WORKFLOW) {
      search.push({
        predicate: "http://www.w3.org/2002/07/owl#deprecated",
        invert: true,
        value: 'true',
        isText: true,
        isExactMatch: true,
        datatype: "http://www.w3.org/2001/XMLSchema#boolean"
      });
    }
    // The following two help avoid deleted and deprecated concept/label resources
    else if (this.state.tabValue === TASKS) {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/isTaskFor",
        join: [
          {
            predicate: "http://www.w3.org/2002/07/owl#deprecated",
            invert: true,
            value: 'true',
            isText: true,
            isExactMatch: true,
            datatype: "http://www.w3.org/2001/XMLSchema#boolean"
          },
          {
            predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
            ...(this.state.scheme !== "All") && { value: this.state.scheme },
            join: [{
              predicate: "https://grafsync.graphifi.com/schema/gs/activateWorkflow",
              value: 'true',
              isText: true,
              isExactMatch: true,
              datatype: "http://www.w3.org/2001/XMLSchema#boolean"
            }]
          }
        ]
      });
    }
    else {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/vm/suggestionSourceConcept",
        join: [{
          predicate: "http://www.w3.org/2002/07/owl#deprecated",
          invert: true,
          value: 'true',
          isText: true,
          isExactMatch: true,
          datatype: "http://www.w3.org/2001/XMLSchema#boolean"
        }]
      });
    }
    let sort = ["http://www.w3.org/2004/02/skos/core#prefLabel"];
    if (this.state.tabValue === WORKFLOW) {
      if (this.state.showType === "labels") {
        sort = ["http://www.w3.org/2008/05/skos-xl#literalForm"];
      }
    }
    else {
      sort = ["http://purl.org/dc/terms/created"];
    }
    if (this.state.tabValue === WORKFLOW) {
      search.push({
        predicate: sort,
        ...(this.state.text !== "") && {
          value: this.state.text,
          isExactMatch: false
        }
      });
    }
    if (this.state.user === "UNASSIGNED") {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/status",
        invert: true,
        value: [APPROVED, REJECTED]
      });
    }
    else {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/status",
        value: [DRAFT, PENDING]
      });
    }
    if (this.state.user === "UNASSIGNED") {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser",
        invert: true
      });
    }
    else if (this.state.user !== "All") {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser",
        value: this.state.user
      });
    }
    else {
      search.push({
        predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser"
      });
    }
    if (this.state.tabValue !== TASKS) {
      search.push({
        predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
        ...(this.state.scheme !== "All") && { value: this.state.scheme },
        ...(this.state.tabValue !== SUGGESTIONS) && {
          join: [{
            predicate: "https://grafsync.graphifi.com/schema/gs/activateWorkflow",
            value: 'true',
            isText: true,
            isExactMatch: true,
            datatype: "http://www.w3.org/2001/XMLSchema#boolean"
          }]
        }
      });
    }
    const data = await this.doSearch({
      search,
      predicates: [
        "http://www.w3.org/2004/02/skos/core#prefLabel",
        "http://www.w3.org/2004/02/skos/core#inScheme",
        "http://www.w3.org/2008/05/skos-xl#literalForm",
        "https://grafsync.graphifi.com/schema/gs/assignedLink",
        "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
        "http://purl.org/dc/terms/created"
      ],
      page,
      pageSize: 20,
      ...(this.props.data.skosXL && this.state.showType === "concepts") && { entailment: ["propertyChain"] },
      ...(this.state.tabValue === WORKFLOW) && { language: this.state.lang },
      graph: [this.props.graph],
      basicSort: true,
      sort
    }, searchID).promise;
    return data;
  };

  loadTaskCounts = async (resources) => {
    const resourceList = resources.map(r => r.id);
    let taskList = [];
    resourceList.forEach(r => {
      const data = GS.data(r, this.props.graph, false);
      if (data && data.hasTask) taskList = taskList.concat(data.hasTask);
    });
    let taskCountData = {};
    if (taskList.length === 0) {
      return taskCountData;
    }
    let search = [
      {
        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
        value: "https://grafsync.graphifi.com/schema/gs/Task",
      }
    ];
    const data = await this.doSearch({
      search,
      from: taskList,
      predicates: [
        "https://grafsync.graphifi.com/schema/gs/isTaskFor",
        "https://grafsync.graphifi.com/schema/gs/status"
      ],
      pageSize: 200,
      graph: [this.props.graph],
      basicSort: true
    }).promise;
    resourceList.forEach(r => {
      const resourceTasks = data.results.filter(d => d.isTaskFor === r && d.status);
      const pending = resourceTasks.filter(t => t.status[0] === PENDING).length;
      const rejected = resourceTasks.filter(t => t.status[0] === REJECTED).length;
      const approved = resourceTasks.filter(t => t.status[0] === APPROVED).length;
      taskCountData[r] = {
        pending,
        rejected,
        approved
      };
    });
    return taskCountData;
  };

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  handlePageChange = (page) => {
    const searchID = uuidv4();
    this.setState({
      page,
      refresh: false,
      searchID
    }, () => this.getData(searchID));
  };

  changeTab = (tabValue) => {
    const searchID = uuidv4();
    // We need to force isLoading here to avoid rendering when tabs switch
    this.setState({
      tabValue,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined
    }, () => this.getData(searchID));
  };

  handleRefresh = () => this.setState({ refresh: true });

  doRefresh = () => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined
    }, () => this.getData(searchID));
  };

  handleShowTypeChange = (event) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      showType: event.target.value
    }, () => this.getData(searchID));
  };

  changeLanguage = (lang) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      lang
    }, () => this.getData(searchID));
  };

  handleFilterChange = (text) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      text
    }, () => this.getData(searchID));
  };

  handleSuggestionTypeChange = (suggestionType) => {
    const searchID = uuidv4();
    this.setState({
      page: 1,
      searchID,
      refresh: false,
      pageCount: undefined,
      searchCount: undefined,
      resources: undefined,
      suggestionType
    }, () => this.getData(searchID));
  };

  render() {

    const { classes, data, project, graph, projectUsers, user, taxonomies, entitlements } = this.props;
    const { usable, isLoading, isErrored, resources, scheme, taskStatusCounts, lang,
      page, pageCount, pageSize, searchCount, tabValue, refresh, showType, text, schemeIsActivated } = this.state;

    if (!usable) return <div data-test="no-access" style={{ paddingTop: 60, paddingRight: 30, width: '100%', textAlign: 'center' }}>
      <Typography align="center" variant="body2">Jobs Manager is not available with your current subscription.</Typography>
      <Typography align="center" variant="body2">This feature requires a minimum of a Premium subscription.</Typography>
      <Upgrade />
    </div>;

    let tabItems = [
      {
        icon: AssignmentTurnedInIcon,
        label: "Workflow"
      }
    ];

    if (entitlements.includes(ENTITLEMENTS.VIEW_TASK) || entitlements.includes(ENTITLEMENTS.EDIT_TASK) || entitlements.includes(ENTITLEMENTS.CREATE_TASK)) {
      tabItems.push({
        icon: AssignmentIcon,
        label: "Tasks"
      });
    }
    if (entitlements.includes(ENTITLEMENTS.VIEW_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MAKE_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS)) {
      tabItems.push({
        icon: EmojiObjectsIcon,
        label: "Suggestions"
      });
    }

    if (isErrored) return <Errored dataTest="tm-error" data={isErrored} />;

    const taxonomyList = taxonomies ? taxonomies.map(t => t.id) : [];
    const isWorkflowActivated = taxonomies && scheme !== "All" ? schemeIsActivated : undefined;
    const fromResult = (page - 1) * pageSize + 1;
    const toResult = page * pageSize > searchCount ? searchCount : page * pageSize;

    const headers = tabValue === WORKFLOW ?
      ["Resource", "Taxonomy", "Status", "Assigned To", "Instructions", "Tasks", "Action", "Reassign"]
      :
      tabValue === TASKS ?
        ["Resource", "Taxonomy", "Status", "Assigned To", "Instructions", "Reassign"]
        :
        ["Placed On", "Taxonomy", "Status", "Suggestion Type", "Suggested Label", "Assigned To", "Detail", "Reassign"];

    return (
      <div style={{ paddingBottom: 30, minWidth: 800 }}>
        <div className={classes.tasks}>
          <div data-test="search-filtering" className={classes.filterArea}>
            <Grid container spacing={2} classes={
              {
                "spacing-xs-2": this.props.classes.fullGrid
              }
            }>
              <Grid item xs={4}>
                <Typography className={classes.mainHeading} style={{ paddingTop: 0 }}>Jobs Manager</Typography>
              </Grid>
              <Grid item xs={2}>
                <TaxonomySelector
                  id={project}
                  defaultLang={data.defaultLang}
                  displayWorkflow={true}
                  taxonomies={taxonomyList}
                  disabled={isLoading}
                  graph={graph}
                  handleChange={this.handleSchemeChange} />
              </Grid>
              <Grid item xs={2}>
                <ProjectUsers
                  disabled={isLoading}
                  selected={user.id}
                  label="Assigned To"
                  additionalOptions={[
                    {
                      value: "UNASSIGNED",
                      label: "Unassigned"
                    }
                  ]}
                  projectUsers={projectUsers}
                  onUserChange={this.handleUserChange} />
              </Grid>
            </Grid>
          </div>
          <div>
            <div className={classes.taskResults}>
              <InAppBar
                tabValue={tabValue}
                background={false}
                onChangeTab={this.changeTab}
                tabItems={tabItems} />
              <div className={classes.taskSection}>
                {tabValue !== TASKS &&
                  <div data-test="search-filtering" className={classes.filterArea}>
                    <Grid container spacing={2} classes={
                      {
                        "spacing-xs-2": this.props.classes.fullGrid
                      }
                    }>
                      {tabValue === WORKFLOW &&
                        <React.Fragment>
                          <Grid item xs={12} sm={4} md={3}>
                            <div style={{ paddingTop: 3 }}>
                              <PagingFilter
                                autoFocus={true}
                                disabled={isLoading}
                                handleFilterChange={this.handleFilterChange}
                                text={text}
                                minLength={2}
                                includeLabel={true}
                                label="Label"
                                maxLength={50} />
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={4} md={3}>
                            <ProjectLanguageSelector
                              key={lang}
                              selectedLang={lang}
                              includeLabel={true}
                              disabled={isLoading}
                              onSelect={this.changeLanguage} />
                          </Grid>
                          {data.skosXL &&
                            <Grid item xs={12} sm={4} md={6}>
                              <div style={{ marginTop: 16, textAlign: 'right' }}>
                                <FormControl component="fieldset">
                                  <RadioGroup row aria-label="Select concepts or XL labels" name="showType" value={showType} onChange={this.handleShowTypeChange}>
                                    <FormControlLabel disabled={isLoading} data-test="show-concepts" value="concepts" control={<Radio color="primary" />} label="Show Concepts"
                                      classes={{ label: classes.formLabelInline }}
                                    />
                                    <FormControlLabel disabled={isLoading} data-test="show-labels" value="labels" control={<Radio color="primary" />} label="Show XL Labels"
                                      classes={{ label: classes.formLabelInline }}
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </div>
                            </Grid>
                          }
                        </React.Fragment>
                      }
                      {tabValue === SUGGESTIONS &&
                        <Grid item xs={12} sm={4} md={3}>
                          <MenuSelector
                            dataTest="type-selector"
                            label="Suggestion Type"
                            disabled={isLoading}
                            selected="All"
                            handleChange={this.handleSuggestionTypeChange}
                            hintWord="suggestion type"
                            options={[
                              {
                                "value": "All",
                                "label": "All"
                              },
                              ...SUGGESTION_TYPE_OPTIONS
                            ]} />
                        </Grid>
                      }
                    </Grid>
                  </div>
                }
                {isLoading ?
                  <div style={{ height: 250 }}>
                    <Loading offset="120px" />
                  </div>
                  :
                  !resources || resources.length === 0 ?
                    isWorkflowActivated !== false || tabValue === SUGGESTIONS ?
                      <div style={{ height: 140 }}>
                        <ActionText offset="120px">
                          {page > 1 ?
                            <span>Something has changed. Try refreshing!</span>
                            :
                            <span>Nothing to do!</span>
                          }
                        </ActionText>
                        {page > 1 &&
                          <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <PrimaryButton dataTest="refresh" onClick={this.doRefresh}>Refresh</PrimaryButton>
                          </div>
                        }
                      </div>
                      :
                      <div style={{ height: 140 }}>
                        <ActionText offset="120px">Workflow is not activated for this taxonomy.</ActionText>
                      </div>
                    :
                    <div style={{ marginTop: 12, overflowX: 'scroll' }}>
                      <div>
                        {searchCount &&
                          <Typography
                            data-test="showing-results"
                            variant="body2"
                            style={{ marginLeft: 6 }}
                            className={classes.showing}>Showing {fromResult} to {toResult} of {searchCount}</Typography>
                        }
                      </div>
                      <TableContainer style={{
                        marginTop: 6,
                        overflowX: "initial"
                      }}>
                        <Table data-test="jobs-viewer">
                          {resources.length > 0 &&
                            <React.Fragment>
                              <TableHead data-test="jobs-header">
                                <TableRow>
                                  {
                                    headers.map((col, index) => {
                                      return <TableCell
                                        data-test={"header-" + index}
                                        key={index}
                                        component="th"
                                        align={(index === 5 && [TASKS].includes(tabValue)) || index === 7 ?
                                          'right'
                                          :
                                          (index === 5 || index === 6) && tabValue === WORKFLOW ?
                                            'center'
                                            :
                                            'left'}
                                        style={{ ...(tabValue === WORKFLOW && index === 5) && { width: 136 } }}
                                        className={classes.tableHeading}>
                                        <span>
                                          {col}
                                        </span>
                                      </TableCell>;
                                    })}
                                </TableRow>
                              </TableHead>
                              <TableBody data-test="jobs-viewer-list">
                                {resources.map(resource => {
                                  return tabValue === WORKFLOW ?
                                    <ResourceDisplay
                                      id={resource.id}
                                      key={resource.id}
                                      viewLang={lang}
                                      taxonomies={this.props.taxonomies}
                                      defaultLang={data.defaultLang}
                                      project={project}
                                      user={user.id}
                                      onChange={this.handleRefresh}
                                      assignedFilter={this.state.user}
                                      taskStatusCounts={taskStatusCounts ? taskStatusCounts[resource.id] : undefined}
                                      entitlements={entitlements}
                                      suppressLoading={true}
                                      projectUsers={projectUsers}
                                      graph={graph} />
                                    :
                                    tabValue === TASKS ?
                                      <TaskDisplay
                                        suppressLoading={true}
                                        user={user.id}
                                        project={project}
                                        taxonomies={this.props.taxonomies}
                                        projectUsers={projectUsers}
                                        entitlements={entitlements}
                                        viewLang={data.defaultLang}
                                        onChange={this.handleRefresh}
                                        defaultLang={data.defaultLang}
                                        id={resource.id}
                                        key={resource.id}
                                        graph={graph}
                                      />
                                      :
                                      <SuggestionDisplay
                                        id={resource.id}
                                        key={resource.id}
                                        taxonomies={this.props.taxonomies}
                                        viewLang={data.defaultLang}
                                        defaultLang={data.defaultLang}
                                        project={project}
                                        assignedFilter={this.state.user}
                                        entitlements={entitlements}
                                        user={user.id}
                                        suppressLoading={true}
                                        onChange={this.handleRefresh}
                                        projectUsers={projectUsers}
                                        setToApproved={this.setToApproved}
                                        graph={graph} />;
                                })}
                              </TableBody>
                            </React.Fragment>
                          }
                        </Table>
                      </TableContainer>
                      {refresh &&
                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                          <PrimaryButton dataTest="refresh" onClick={this.doRefresh}>Refresh</PrimaryButton>
                        </div>
                      }
                      {!refresh && resources && resources.length > 0 && pageCount > 1 &&
                        <div style={{ marginTop: '20px' }}>
                          <Paging page={page} pageCount={pageCount} pageItemsRange={5} handlePageChange={this.handlePageChange} />
                        </div>
                      }
                    </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

TaskManager.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  projectUsers: PropTypes.array.isRequired,
  projectType: PropTypes.string.isRequired,
  entitlements: PropTypes.array.isRequired,
  taxonomies: PropTypes.array,
  user: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired
};

export default withStyles(styles)(TaskManager);